import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AuthLayout } from "@/components/AuthLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { openRazorpay } from "@/lib/razorpay";
import { SIGNUP_TYPES } from "@/lib/constants";

const ModeSwitch = ({ isClient, onChange }) => (
  <div className="grid grid-cols-2 rounded-full bg-tm-muted p-1" data-testid="signup-mode-switch">
    {[[false, "Professional"], [true, "Client / Business"]].map(([v, l]) => (
      <button key={l} type="button" onClick={() => onChange(v)} data-testid={`signup-mode-${v ? "client" : "professional"}`}
        className={`rounded-full py-2 text-sm font-semibold transition-colors duration-200 ${isClient === v ? "bg-tm-green text-white" : "text-slate-600"}`}>{l}</button>
    ))}
  </div>
);

const TypePicker = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
    {SIGNUP_TYPES.map((t) => (
      <button key={t.value} type="button" onClick={() => onChange(t.value)} data-testid={`account-type-${t.value}`}
        className={`rounded-xl border-2 p-3 text-left transition-colors duration-200 ${value === t.value ? "border-tm-green bg-tm-green/5" : "border-border bg-white hover:border-tm-gold"}`}>
        <p className="text-sm font-semibold text-tm-green">{t.title}</p>
        <p className="text-[11px] leading-tight text-slate-500">{t.text}</p>
      </button>
    ))}
  </div>
);

function PlanPicker({ plans, offer, value, onChange }) {
  if (!plans.length) return null;
  return (
    <div className="space-y-2" data-testid="signup-plan-picker">
      <Label>Membership plan</Label>
      {offer?.trial_active && <p className="rounded-lg bg-orange-50 px-3 py-2 text-xs text-orange-800" data-testid="signup-offer-note">{offer.note}</p>}
      <div className="grid grid-cols-2 gap-2">
        {plans.map((p) => (
          <button key={p.id} type="button" onClick={() => onChange(p.id)} data-testid={`signup-plan-${p.id}`}
            className={`rounded-xl border-2 p-3 text-left ${value === p.id ? "border-tm-green bg-tm-green/5" : "border-border bg-white"}`}>
            <p className="text-sm font-semibold text-tm-green">{p.label}</p><p className="text-xs text-slate-500">{p.display}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Register() {
  const { user, acceptSession } = useAuth();
  const [params, setParams] = useSearchParams();
  const isClient = params.get("as") === "client";
  const [form, setForm] = useState({ account_type: "CA", full_name: "", mobile_number: "", email: "", password: "", city: "", membership_number: "" });
  const [plans, setPlans] = useState({ plans: [], offer: null });
  const [plan, setPlan] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (isClient) return;
    api.get("/membership/plans", { params: { account_type: form.account_type } }).then(({ data }) => {
      setPlans(data);
      setPlan(data.plans?.[0]?.id || "");
    });
  }, [form.account_type, isClient]);

  if (user) return <Navigate to="/feed" replace />;

  const proSignup = async (body) => {
    await api.post("/auth/register/pending", body).catch(() => {});
    if (plans.offer?.trial_active) return (await api.post("/auth/register/professional/free", body)).data;
    const { data: order } = await api.post("/auth/register/professional/order", body);
    const resp = await openRazorpay(order);
    if (!resp) throw new Error("Payment was cancelled");
    return (await api.post("/auth/register/professional/verify", resp)).data;
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const body = isClient
      ? { full_name: form.full_name, mobile_number: form.mobile_number, password: form.password, city: form.city || null, account_type: "CLIENT" }
      : { ...form, email: form.email || null, membership_number: form.membership_number || null, qualification: form.account_type };
    try {
      const data = isClient ? (await api.post("/auth/register", body)).data : await proSignup({ ...body, plan });
      await acceptSession(data);
      toast.success("Welcome to Taxmandi!");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={isClient ? "Join as a client or business" : "Join as a professional"}
      subtitle={isClient ? "Free forever. Sign up with your name and mobile number to connect with CAs, CSs and tax experts." : "For CA, CS, CMA, tax and accounting professionals and students"}>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <ModeSwitch isClient={isClient} onChange={(c) => setParams(c ? { as: "client" } : {})} />
        {!isClient && <TypePicker value={form.account_type} onChange={(account_type) => setForm({ ...form, account_type })} />}
        <div className="space-y-2"><Label>Full name</Label><Input required value={form.full_name} onChange={set("full_name")} className="h-11" data-testid="register-name-input" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2"><Label>Mobile number</Label><Input required inputMode="numeric" pattern="[0-9]{10}" title="10-digit mobile number" value={form.mobile_number} onChange={set("mobile_number")} className="h-11" data-testid="register-mobile-input" /></div>
          <div className="space-y-2"><Label>City</Label><Input value={form.city} onChange={set("city")} className="h-11" data-testid="register-city-input" /></div>
        </div>
        {!isClient && <div className="space-y-2"><Label>Email (optional)</Label><Input type="email" value={form.email} onChange={set("email")} className="h-11" data-testid="register-email-input" /></div>}
        <div className="space-y-2"><Label>{isClient ? "Create a password (for login)" : "Password"}</Label><Input type="password" required minLength={6} value={form.password} onChange={set("password")} className="h-11" data-testid="register-password-input" /></div>
        {!["CA", "CS", "CMA"].includes(form.account_type) || isClient ? null : (
          <div className="space-y-2"><Label>Membership number (optional)</Label><Input value={form.membership_number} onChange={set("membership_number")} className="h-11" data-testid="register-membership-number-input" /></div>
        )}
        {!isClient && <PlanPicker plans={plans.plans || []} offer={plans.offer} value={plan} onChange={setPlan} />}
        {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="register-error">{error}</p>}
        <Button type="submit" disabled={busy} className="h-11 w-full rounded-full bg-tm-green hover:bg-tm-green/90" data-testid="register-submit-button">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{isClient ? "Sign up free" : plans.offer?.trial_active ? "Create account" : "Continue to payment"}
        </Button>
      </form>
      <p className="mt-8 text-center text-sm text-slate-600">Already a member? <Link to="/login" className="font-semibold text-tm-green-light hover:underline" data-testid="goto-login-link">Log in</Link></p>
    </AuthLayout>
  );
}
